import { useEffect, useState } from "react";

import { axiosInstance } from "./useAxios";

import { UserIdentity } from "../api/user/user";

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  userId: string | null;
  email: string | null;
}

let globalAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  userId: null,
  email: null,
};
let authCheckPromise: Promise<void> | null = null;
let hasCheckedAuth = false;
const listeners = new Set<(state: AuthState) => void>();

const notifyListeners = (state: AuthState) => {
  globalAuthState = state;
  listeners.forEach((listener) => listener(state));
};

const checkAuthOnce = async (): Promise<void> => {
  if (typeof window === "undefined") {
    return;
  }

  if (hasCheckedAuth) {
    return;
  }

  if (authCheckPromise) {
    return authCheckPromise;
  }

  hasCheckedAuth = true;
  authCheckPromise = (async () => {
    try {
      const response = await axiosInstance.get<UserIdentity>("/user/identity");
      notifyListeners({
        isAuthenticated: true,
        isLoading: false,
        userId: response.data.id,
        email: response.data.email!,
      });
    } catch {
      notifyListeners({
        isAuthenticated: false,
        isLoading: false,
        userId: null,
        email: null,
      });
    }
  })();

  return authCheckPromise;
};

export const useAuth = (): AuthState => {
  const [authState, setAuthState] = useState<AuthState>(globalAuthState);

  useEffect(() => {
    const listener = (state: AuthState) => {
      setAuthState(state);
    };
    listeners.add(listener);

    void checkAuthOnce();

    return () => {
      listeners.delete(listener);
    };
  }, []);

  return authState;
};

export const resetAuthState = (): void => {
  hasCheckedAuth = false;
  authCheckPromise = null;
  globalAuthState = {
    isAuthenticated: false,
    isLoading: true,
    userId: null,
    email: null,
  };
  notifyListeners(globalAuthState);
};
